import { EventEmitter, Injectable } from '@angular/core';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import turfCircle from '@turf/circle';
import { Feature, FeatureCollection, Polygon } from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import { Observable, Subscriber } from 'rxjs';
import { MapService } from 'src/app/shared/components/map/map.service';
import { mapBoxDrawStyle } from './style/mapboxdraw-style';

@Injectable({
  providedIn: 'root'
})
export class MapDrawService {

  private draw: MapboxDraw;

  create$: Observable<Feature<Polygon>>;
  update$: Observable<Feature<Polygon>>;
  delete$: Observable<Feature<Polygon>>;

  private createEmmiter = new EventEmitter<Feature<Polygon>>();

  constructor(
    mapService: MapService
  ) {
    mapService.map$.subscribe(m => this.createDraw(m));
  }

  private createDraw(mapbox: mapboxgl.Map) {
    this.draw = new MapboxDraw({
      displayControlsDefault: true,
      userProperties: true,
      modes: this.createModes(),
      styles: mapBoxDrawStyle
    });

    mapbox.addControl(this.draw, 'top-left');

    this.create$ = new Observable((observer) => {
      mapbox.on('draw.create', e => this.next(e, observer));
      this.createEmmiter.subscribe(e => {
        observer.next(e);
      });
    });

    this.update$ = new Observable((observer) => {
      mapbox.on('draw.update', e => this.next(e, observer));
    });

    this.delete$ = new Observable((observer) => {
      mapbox.on('draw.delete', e => this.next(e, observer));
    });
  }

  private next(e, observer: Subscriber<Feature<Polygon>>) {
    const features = this.getFeatures(e);
    if (features.length === 0) {
      return;
    }
    const feature = features[0];
    if (feature.geometry.type === 'Polygon') {
      observer.next(feature as Feature<Polygon>);
    }
  }

  private getFeatures(e) {
    return e.features as Feature[];
  }

  addArea(feature: Feature<Polygon>) {
    this.draw.add(feature);
  }

  addAreaAndNotify(feature: Feature<Polygon>) {
    this.addArea(feature);
    this.createEmmiter.next(feature);
  }

  addPoint(lng: number, lat: number) {
    const id = 'POINT_' + Math.round(Math.random() * 99999);
    const point: Feature = {
      id,
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [lng, lat]
      }
    };
    this.draw.add(point);
    return id;
  }

  addCircle(lng: number, lat: number) {
    const id = 'CIRCLE_' + Math.round(Math.random() * 99999);
    const center = [lng, lat];
    const radius = 0.05;
    const options = { steps: 10, units: 'kilometers', properties: { foo: 'bar' } };
    const circle = turfCircle(center, radius, options) as Feature<Polygon>;
    circle.id = id;
    this.addArea(circle);
    return id;
  }

  enableArea() {
    this.draw.changeMode(MODES.DRAW_POLYGON);
  }

  disable() {
    this.removeHighlight();
    this.draw.changeMode(MODES.STATIC);
  }

  enable() {
    this.draw.changeMode(MODES.SIMPLE_SELECT);
  }

  delete(featureId: string): void {
    this.draw.delete(featureId);
  }

  clear() {
    if (this.draw) {
      this.draw.deleteAll();
    }
  }

  hasSelected() {
    return this.draw.getSelectedIds().length > 0;
  }

  highlight(featureId: string | number) {
    const collection = this.draw.getAll() as FeatureCollection;
    this.removeHighlightProperty(collection);

    const feature = collection.features.find(f => f.id === featureId);
    feature.properties = {
      ...feature.properties,
      mitraHighlight: true
    };
    this.repaint(collection);
  }

  private removeHighlight() {
    const collection = this.draw.getAll() as FeatureCollection;
    this.removeHighlightProperty(collection);
  }

  private animate(cb: AnimationCallback) {
    const collection = this.draw.getAll() as FeatureCollection;
    const repeat = cb(collection);
    this.repaint(collection);
    if (repeat) {
      requestAnimationFrame(() => this.animate(cb));
    }
  }

  private repaint(collection: FeatureCollection) {
    this.draw.set(collection);
    this.draw.changeMode(this.draw.getMode());
  }

  private removeHighlightProperty(collection: FeatureCollection) {
    collection.features.find(f => {
      if (f.properties.mitraHighlight) {
        f.properties.mitraHighlight = false;
        return true;
      }
    });
  }

  private createModes() {
    const modes = MapboxDraw.modes;
    modes.static = StaticMode;
    return modes;
  }
}

const MODES = {
  DRAW_LINE_STRING: 'draw_line_string',
  DRAW_POLYGON: 'draw_polygon',
  DRAW_POINT: 'draw_point',
  SIMPLE_SELECT: 'simple_select',
  DIRECT_SELECT: 'direct_select',
  STATIC: 'static'
};

export type AnimationCallback = (collection: FeatureCollection) => boolean;
